import {
  CarrierType,
  EquipmentType,
  JobDto,
  OperationType,
  RailcarTrackPositionDto,
  WorkAreaType,
  WorkInstructionWarningReason,
  YardBlockEquipmentAssignmentDto,
} from '@operations/app/api'
import { JobStore } from '@operations/stores/JobStore'
import _ from 'lodash'

import {
  DefaultJobSizes,
  JobTypes,
  doesJobOperationTypeOrCarrierMatchesJobType,
} from '@operations/app/models/operator-pages'
import { EquipmentOperatorLandingUIStore } from '@operations/features/equipmentOperator/stores/equipment-operator-landing.ui-store'
import { EquipmentStore } from '@operations/stores/EquipmentStore'
import { WorkInstructionStore } from '@operations/stores/WorkInstructionStore'
import { escapeTruckNumber } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { action, computed, makeObservable, observable } from 'mobx'
import { JobGroupDto, JobGroupType } from '../models/job-group.model'
import {
  JobNavigationDto,
  YardBlockEquipmentAssignmentOptionDto,
} from '../models/job-navigation.model'
import { isCraneEquipmentType } from '../utils'
import { EquipmentOperatorSearchUIStore } from './equipment-operator-search.ui-store'

export class EquipmentOperatorUIStore {
  hiddenJobWorkInstructionIds: number[] = []
  isContainerOperation = true
  jobType: JobTypes = JobTypes.all
  nonNumeric?: boolean
  selectedEquipmentId?: number
  railcarTrackPositions: Record<number, RailcarTrackPositionDto[]> = {}

  selectedYardBlockId?: string
  selectedBays: string[] = []
  selectedVesselBays: string[] = []
  notPlanned?: boolean
  selectedContainerSize?: number | string

  isFullView = false

  defaultFilter: { jobType: JobTypes; selectedYardBlockId?: string; selectedBays: string[] } = {
    jobType: JobTypes.all,
    selectedYardBlockId: undefined,
    selectedBays: [],
  }

  constructor(
    private jobStore: JobStore,
    private equipmentStore: EquipmentStore,
    private workInstructionStore: WorkInstructionStore,
    private equipmentOperatorSearchUIStore: EquipmentOperatorSearchUIStore,
    public equipmentOperatorLandingUIStore: EquipmentOperatorLandingUIStore,
  ) {
    makeObservable(this, {
      jobType: observable,
      hiddenJobWorkInstructionIds: observable,
      isContainerOperation: observable,
      nonNumeric: observable,
      notPlanned: observable,
      selectedEquipmentId: observable,
      railcarTrackPositions: observable,
      selectedYardBlockId: observable,
      selectedBays: observable,
      selectedVesselBays: observable,
      selectedContainerSize: observable,
      isFullView: observable,
      defaultFilter: observable,

      closeSearchIfFinishingLastNotification: action,
      resetToDefault: action,
      hideJob: action,
      loadJobs: action,
      selectEquipment: action,
      setIsContainerOperation: action,
      setJobType: action,
      setNonNumeric: action,
      setNotPlanned: action,
      setOperationType: action,
      setYardBlockId: action,
      setSelectedBays: action,
      setSelectedVesselBays: action,
      setWagonWeights: action,
      showJob: action,
      setSelectedContainerSize: action,
      setFullView: action,
      clearFilters: action,

      jobs: computed,
      carrierTypeFilteredJobsNavigationDto: computed,
      equipment: computed,
      filteredJobs: computed,
      subtitle: computed,
      hasJobNavigation: computed,
      selectedYardBlockJobs: computed,
      typeOfSelectedEquipment: computed,
      vesselBays: computed,
      yardBlocksAssignments: computed,
      hasAnySelectedFilter: computed,
      isAtDefault: computed,
    })
  }

  loadJobs = async () => {
    await this.jobStore.loadJobsByEquipmentId(this.selectedEquipmentId)
  }

  public async selectEquipment(id?: number) {
    if (this.selectedEquipmentId !== id) {
      this.selectedEquipmentId = id
      await this.loadJobs()
    }
  }

  public setIsContainerOperation(value: boolean) {
    if (this.isContainerOperation !== value) {
      this.isContainerOperation = value
    }
  }

  public setNonNumeric(value?: boolean) {
    if (this.nonNumeric !== value) {
      this.nonNumeric = value

      if (value) {
        this.selectedYardBlockId = undefined
        this.selectedBays = []

        if (this.jobType === JobTypes.internal || this.jobType === JobTypes.service)
          this.initializerJobType()
      }
    }
  }

  public setNotPlanned(value?: boolean) {
    if (this.notPlanned !== value) {
      this.notPlanned = value
      this.selectedYardBlockId = undefined
      this.selectedBays = []
      this.nonNumeric = false
      this.initializerJobType()
    }
  }

  public setJobType = (jobType: JobTypes, resetOperationTypeFilter = true) => {
    if (jobType !== this.jobType) {
      this.jobType = jobType

      if (resetOperationTypeFilter) {
        this.equipmentOperatorLandingUIStore.selectOperationType(null)

        if (this.jobType === JobTypes.internal || this.jobType === JobTypes.service) {
          this.nonNumeric = false
        }
      }
    }
  }

  public setOperationType(operationType?: OperationType): void {
    this.equipmentOperatorLandingUIStore.selectOperationType(operationType ?? null)
  }

  public async setDefaultTypeAndArea() {
    const assignedYardBlock = this.yardBlocksAssignments.find(y =>
      y.equipments.some(e => e.isPlanned && e.id === this.selectedEquipmentId),
    )

    this.setYardBlockId(assignedYardBlock?.yardBlockId)

    const yardEquipmentWorkArea = this.equipment.workAreaEquipments?.find(
      x => x.yardBlockId === assignedYardBlock?.yardBlockId,
    )

    if (yardEquipmentWorkArea) {
      this.setSelectedBays(yardEquipmentWorkArea.assignedBays)
    }

    const carrierEquipmentWorkArea = this.equipment.workAreaEquipments?.find(
      x =>
        x.isInOperation &&
        (x.workAreaType === WorkAreaType.Crane || x.workAreaType === WorkAreaType.Train),
    )

    if (carrierEquipmentWorkArea) {
      this.setJobType(
        carrierEquipmentWorkArea.workAreaType === WorkAreaType.Crane
          ? JobTypes.vessel
          : JobTypes.train,
      )
    }

    this.defaultFilter = {
      jobType: this.jobType,
      selectedYardBlockId: this.selectedYardBlockId,
      selectedBays: [...this.selectedBays],
    }
  }

  public async resetToDefault() {
    this.clearFilters()
    await this.setDefaultTypeAndArea()
  }

  public async setYardBlockId(id?: string) {
    if (this.selectedYardBlockId !== id) {
      this.selectedYardBlockId = id
      this.selectedBays = []
      this.notPlanned = false
      this.nonNumeric = false
    }
  }

  public async setSelectedBays(bays: string[]) {
    this.selectedBays = bays
  }
  public async setSelectedVesselBays(bays: string[]) {
    this.selectedVesselBays = bays
  }

  public async setSelectedContainerSize(size?: number | string) {
    this.selectedContainerSize = size
  }

  public async setWagonWeights(carrierVisitId: number) {
    this.railcarTrackPositions[carrierVisitId] =
      await this.workInstructionStore.getRailcarTrackPositions(carrierVisitId)
  }

  public hideJob(workInstructionId?: number) {
    if (workInstructionId) {
      this.hiddenJobWorkInstructionIds = [...this.hiddenJobWorkInstructionIds, workInstructionId]
    }
  }

  public showJob(workInstructionId?: number) {
    this.hiddenJobWorkInstructionIds = this.hiddenJobWorkInstructionIds.filter(
      x => x !== workInstructionId,
    )
  }

  public selectVesselIfCraneOperator() {
    if (this.jobs.length === 0) {
      return
    }
    if (isCraneEquipmentType(this.typeOfSelectedEquipment)) {
      this.setJobType(JobTypes.vessel, false)
    }
  }

  public closeSearchIfFinishingLastNotification(workInstructionId: number) {
    if (
      this.equipmentOperatorSearchUIStore.isAlertOpen &&
      this.equipmentOperatorSearchUIStore.selectedJobWorkInstructionIds?.includes(
        workInstructionId,
      ) &&
      this.alertFilteredJobs.flatMap(x => x.jobs).length === 0
    ) {
      this.equipmentOperatorSearchUIStore.toggleSearch()
    }
  }

  public setFullView(value: boolean) {
    if (this.isFullView !== value) this.isFullView = value
  }

  public get jobs() {
    const jobs = this.equipmentOperatorLandingUIStore.selectedVesselVisit
      ? this.jobStore.jobAndYardBlocksDto.jobs.filter(
          j => j.carrierVisit?.id === this.equipmentOperatorLandingUIStore.selectedVesselVisit!.id,
        )
      : this.jobStore.jobAndYardBlocksDto.jobs

    const confirmedJobs = filterPlannedJobs(jobs)

    return orderJobsWithDeadline(confirmedJobs).filter(
      x => !this.hiddenJobWorkInstructionIds.includes(x.workInstructionId),
    )
  }

  public get yardBlocksAssignments() {
    const items = this.jobStore.jobAndYardBlocksDto.yardBlockEquipmentAssignments

    const yardBlocks = items.reduce<YardBlockEquipmentAssignmentOptionDto[]>((list, item) => {
      const yardJobs = this.jobs.filter(x => isJobWithinYardBlock(x, item))

      const plannedTotal = yardJobs.filter(x => x.isPlanned).length

      if (yardJobs.length - plannedTotal > 0) {
        list.push({
          ...item,
          jobAmount: yardJobs.length - plannedTotal,
        })
      }

      return list
    }, [])

    return yardBlocks
  }

  public get vesselBays() {
    return _(
      this.jobs
        .filter(x => x.vesselBay !== undefined && x.vesselBay !== null)
        .map(x => x.vesselBay!.toString()),
    )
      .uniq()
      .sort()
      .value()
  }

  public get equipment() {
    return this.equipmentStore.items.find(x => x.id === this.selectedEquipmentId)!
  }

  public get typeOfSelectedEquipment() {
    return this.equipment?.equipmentType
  }

  public get carrierTypeFilteredJobsNavigationDto(): JobNavigationDto {
    const dischargeAmount = this.jobsFilteredByNavigationFilters.filter(
      x =>
        (!this.nonNumeric || !x.cargoUnit?.displayName) &&
        x.operationType === OperationType.Inbound,
    ).length

    const loadAmount = this.jobsFilteredByNavigationFilters.filter(
      x =>
        (!this.nonNumeric || !x.cargoUnit?.displayName) &&
        x.operationType === OperationType.Outbound,
    ).length

    return {
      dischargeAmount,
      loadAmount,
    }
  }

  public get filteredJobs(): JobGroupDto[] {
    const filteredJobs = this.jobsFilteredByNavigationFilters.filter(
      x =>
        (!this.nonNumeric || !x.cargoUnit?.displayName) &&
        (!this.equipmentOperatorLandingUIStore.selectedOperationType ||
          x.operationType === this.equipmentOperatorLandingUIStore.selectedOperationType),
    )

    const searchFilteredJobs = this.filterJobsBySearch(filteredJobs)

    return groupAndSortJobs(searchFilteredJobs, this.jobType, this.typeOfSelectedEquipment)
  }

  private filterJobsBySearch(jobs: JobDto[]): JobDto[] {
    if (!this.equipmentOperatorSearchUIStore.searchText) return jobs

    const searchedContainerLowerCase = this.equipmentOperatorSearchUIStore.searchText.toLowerCase()

    const searchedContainerWithoutSpace = searchedContainerLowerCase.replace(/\s/g, '')

    const truckSearch = escapeTruckNumber(this.equipmentOperatorSearchUIStore.searchText)

    return jobs.filter(
      x =>
        x.cargoUnit?.displayName?.toLowerCase()?.includes(searchedContainerWithoutSpace) ||
        x.order?.referenceNumber?.toLowerCase()?.includes(searchedContainerLowerCase) ||
        x.cargoUnit?.portOfDischarge?.toLowerCase()?.includes(searchedContainerLowerCase) ||
        x.carrierVisit?.carrierName?.toLowerCase()?.includes(searchedContainerLowerCase) ||
        x.carrierVisit?.carrierName?.includes(truckSearch),
    )
  }

  public get selectedYardBlockJobs() {
    return this.jobs.filter(x => this.isJobWithinTheSelectedYardBlockAndBays(x))
  }

  public get alertFilteredJobs() {
    if (this.equipmentOperatorSearchUIStore.selectedJobWorkInstructionIds) {
      return createEmptyGroupToShowAllJobs(
        this.jobs.filter(x =>
          this.equipmentOperatorSearchUIStore.selectedJobWorkInstructionIds?.includes(
            x.workInstructionId,
          ),
        ),
      )
    }

    return []
  }

  public get hasJobNavigation() {
    return (
      this.typeOfSelectedEquipment !== EquipmentType.Tt &&
      this.typeOfSelectedEquipment !== EquipmentType.Sts
    )
  }

  public get subtitle() {
    if (!this.equipment || isCraneEquipmentType(this.equipment?.equipmentType)) {
      return ''
    }

    const cranes = _(
      this.equipment.workAreaEquipments?.filter(x => x.isInOperation && x.crane).map(x => x.crane),
    )
      .uniq()
      .sort()
      .join(', ')
      .trim()

    if (this.equipment?.equipmentType === EquipmentType.Tt) {
      return cranes
    }

    const yardBlocks = _(this.equipment.yardBlocks?.map(x => x.name).filter(x => !!x))
      .uniq()
      .sort()
      .join(', ')
      .trim()

    if (yardBlocks.length && cranes.length) return `${yardBlocks} / ${cranes}`

    return yardBlocks.length ? yardBlocks : cranes
  }

  public get isAtDefault(): boolean {
    return (
      this.jobType === this.defaultFilter.jobType &&
      this.selectedYardBlockId === this.defaultFilter.selectedYardBlockId &&
      _.isEqual(this.selectedBays, this.defaultFilter.selectedBays) &&
      !this.nonNumeric &&
      !this.selectedContainerSize &&
      this.selectedVesselBays.length === 0 &&
      !this.equipmentOperatorSearchUIStore.searchText
    )
  }

  public get hasAnySelectedFilter(): boolean {
    return (
      !!this.equipmentOperatorSearchUIStore.searchText ||
      this.selectedVesselBays.length > 0 ||
      this.jobType !== JobTypes.all ||
      !!this.selectedYardBlockId ||
      !!this.selectedContainerSize ||
      !!this.nonNumeric
    )
  }

  public clearFilters() {
    this.equipmentOperatorSearchUIStore.searchText = undefined
    this.selectedVesselBays = []
    this.jobType = JobTypes.all
    this.selectedYardBlockId = undefined
    this.selectedBays = []
    this.selectedContainerSize = undefined
    this.nonNumeric = undefined
  }

  private get jobsFilteredByJobType() {
    return this.jobs.filter(
      x =>
        !this.hasJobNavigation ||
        doesJobOperationTypeOrCarrierMatchesJobType(
          this.jobType,
          x.operationType,
          x.carrierVisit?.type,
          x.linkedOutboundWorkInstruction?.carrierVisit?.type,
        ),
    )
  }

  private get jobsFilteredByNavigationFilters() {
    if (this.hasJobNavigation) {
      const filteredBySize =
        this.selectedContainerSize === 'others'
          ? this.jobsFilteredByJobType.filter(
              x => !x.cargoUnit?.length || !DefaultJobSizes.includes(x.cargoUnit.length),
            )
          : this.jobsFilteredByJobType.filter(
              x =>
                !this.selectedContainerSize || x.cargoUnit?.length === this.selectedContainerSize,
            )

      return filteredBySize.filter(x => this.isJobWithinTheSelectedYardBlockAndBays(x))
    }

    return this.jobsFilteredByJobType.filter(x => this.isJobWithinTheSelectedVesselBays(x))
  }

  private isJobWithinTheSelectedYardBlockAndBays(job: JobDto) {
    if (this.notPlanned) {
      return isJobNotPlanned(job)
    }

    if (!this.selectedYardBlockId) {
      return true
    }

    const yardBlock = this.yardBlocksAssignments.find(
      x => x.yardBlockId === this.selectedYardBlockId,
    )

    return (
      isJobWithinYardBlock(job, yardBlock) &&
      (this.selectedBays.length === 0 || isJobWithinYardBlockBays(job, this.selectedBays))
    )
  }

  private isJobWithinTheSelectedVesselBays(job: JobDto) {
    return (
      this.selectedVesselBays.length === 0 || isJobWithinVesselBays(job, this.selectedVesselBays)
    )
  }

  private initializerJobType() {
    if (this.jobType !== JobTypes.all) {
      this.jobType = JobTypes.all
    }
  }
}

export const isJobNotPlanned = (job: JobDto) =>
  !job.origin.yardBlockName &&
  !job.destination.yardBlockName &&
  !job.cargoUnit?.plannedDischargeYardBlockId

export const isJobWithinYardBlock = (job: JobDto, yardBlock?: YardBlockEquipmentAssignmentDto) =>
  job.origin.yardBlockName === yardBlock?.yardBlockName ||
  job.destination.yardBlockName === yardBlock?.yardBlockName ||
  job.cargoUnit?.plannedDischargeYardBlockId === yardBlock?.yardBlockId

export const isJobWithinYardBlockBays = (job: JobDto, bays: string[]) =>
  bays.some(x => job.origin.yardBays?.includes(x)) ||
  bays.some(x => job.destination.yardBays?.includes(x))

export const isJobWithinVesselBays = (job: JobDto, bays: string[]) =>
  !!job.vesselBay && bays.includes(job.vesselBay?.toString())

export const groupAndSortJobs = (
  jobs: JobDto[],
  selectedJobType: JobTypes,
  selectedEquipmentType: EquipmentType,
) => {
  if (selectedEquipmentType === EquipmentType.Sts) {
    return groupAndSortJobsByVesselBay(jobs)
  }

  if (selectedJobType === JobTypes.all) {
    const sortedTruckVisitJobs = groupAndSortJobsByTruckVisit(
      jobs.filter(j => j.carrierVisit?.type === CarrierType.Truck),
    )
    const sortedCargoGroupJobs = groupAndSortJobsByCargoGroup(
      jobs.filter(j => j.carrierVisit?.type !== CarrierType.Truck),
    )
    return [
      ...sortedTruckVisitJobs.filter(j => j.isPriority),
      ...sortedCargoGroupJobs.filter(j => j.isPriority),
      ...sortedTruckVisitJobs.filter(j => !j.isPriority),
      ...sortedCargoGroupJobs.filter(j => !j.isPriority),
    ]
  }

  if (selectedJobType === JobTypes.truck) {
    return groupAndSortJobsByTruckVisit(jobs)
  } else {
    return groupAndSortJobsByCargoGroup(jobs)
  }
}

const groupAndSortJobsByVesselBay = (jobs: JobDto[]) => {
  const groupMap = new Map<number, JobGroupDto>()

  for (const job of jobs) {
    const vesselBay = job.vesselBay ?? 0
    let group = groupMap.get(vesselBay)
    if (!group) {
      group = {
        identifier: vesselBay,
        groupName: vesselBay.toString(),
        jobs: [],
        type: JobGroupType.vesselBay,
      }
      groupMap.set(vesselBay, group)
    }
    group.jobs.push(job)
    if (job.isPriority && !group.isPriority) group.isPriority = true
  }

  const sortedJobGroups = Array.from(groupMap.values()).map(group => ({
    ...group,
    jobs: handleGroupedJobsSorting(group.jobs, false),
  }))

  return _.orderBy(
    sortedJobGroups,
    [group => (group.isPriority ? 0 : 1), group => group.identifier],
    ['asc', 'asc'],
  )
}

const groupAndSortJobsByTruckVisit = (jobs: JobDto[]) => {
  const groupMap = new Map<number, JobGroupDto>()

  for (const job of jobs) {
    const carrierVisitId = job.carrierVisit?.id ?? 0

    let group = groupMap.get(carrierVisitId)
    if (!group) {
      group = {
        identifier: carrierVisitId,
        groupName: job.carrierVisit?.carrierName ?? '',
        jobs: [],
        type: JobGroupType.truckVisit,
      }
      groupMap.set(carrierVisitId, group)
    }
    group.jobs.push(job)
    if (job.isPriority && !group.isPriority) group.isPriority = true
  }

  const sortedJobGroups = Array.from(groupMap.values()).map(group => ({
    ...group,
    jobs: handleGroupedJobsSorting(group.jobs, false),
  }))

  return _.orderBy(
    sortedJobGroups,
    [
      group => (group.isPriority ? 0 : 1),
      group => {
        const ata = group.jobs[0]?.carrierVisit?.ata
        return ata ? new Date(ata).getTime() : Number.MAX_SAFE_INTEGER
      },
    ],
    ['asc', 'asc'],
  )
}

const groupAndSortJobsByCargoGroup = (jobs: JobDto[]) => {
  const groupMap = new Map<number, JobGroupDto>()

  for (const job of jobs) {
    const isPriorityId = job.isPriority ? -1 : 0
    const groupId = job.grouping?.groupId ?? isPriorityId

    const groupName = job.grouping?.groupName ?? ''

    let group = groupMap.get(groupId)
    if (!group) {
      group = {
        identifier: groupId,
        groupName,
        jobs: [],
        type: JobGroupType.default,
      }
      groupMap.set(groupId, group)
    }
    group.jobs.push(job)
    if (job.isPriority && !group.isPriority) group.isPriority = true
  }

  const sortedJobGroups = Array.from(groupMap.values()).map(group => ({
    ...group,
    jobs: handleGroupedJobsSorting(group.jobs, true),
  }))

  return _(sortedJobGroups)
    .orderBy(
      [
        group => (group.isPriority ? 0 : 1),
        group => (group.groupName ? 0 : 1),
        group => group?.groupName,
      ],
      ['asc', 'asc', 'asc'],
    )
    .value()
}
export const handleGroupedJobsSorting = (jobs: JobDto[], useSequenceSort?: boolean): JobDto[] => {
  if (!useSequenceSort) {
    return _(jobs)
      .orderBy(j => (j.isPriority ? 0 : 1), 'asc')
      .value()
  }
  const highPriorityJobs = _(jobs)
    .filter(j => j.isPriority)
    .orderBy(j => j.grouping?.sequence)
    .value()

  const otherJobs = _(jobs)
    .filter(j => !j.isPriority)
    .orderBy(j => j.grouping?.sequence)
    .value()

  return [...highPriorityJobs, ...otherJobs]
}

const createEmptyGroupToShowAllJobs = (jobs: JobDto[]): JobGroupDto[] => {
  return [
    {
      identifier: 0,
      groupName: '',
      jobs,
      type: JobGroupType.default,
    },
  ]
}

const executablePlannedWarningReasons: WorkInstructionWarningReason[] = [
  WorkInstructionWarningReason.LinkedInboundForceFinishedByGateOutWithoutLocation,
]

const acceptablePlannedWarningReasons: WorkInstructionWarningReason[] = [
  WorkInstructionWarningReason.RequiresReservation,
  WorkInstructionWarningReason.LoadingApprovalMissing,
  WorkInstructionWarningReason.DischargeApprovalMissing,
  ...executablePlannedWarningReasons,
]

export const isPlannedAndBlocked = (job: JobDto, skipYardPlanning = false): boolean =>
  job.isPlanned &&
  !(job.warningReason && executablePlannedWarningReasons.includes(job.warningReason)) &&
  !(skipYardPlanning && job.warningReason === WorkInstructionWarningReason.RequiresReservation)

export const filterPlannedJobs = (jobs: JobDto[]) => {
  return jobs.filter(
    job =>
      !job.isPlanned ||
      (job.warningReason && acceptablePlannedWarningReasons.includes(job.warningReason)),
  )
}

export const orderJobsWithDeadline = (jobs: JobDto[]) => {
  //First add ordered all jobs with one hour to dead line
  const orderedJobs: JobDto[] = orderJobs(jobsWithDeadline(jobs))

  //Then add ordered rest jobs
  const otherJobs = jobs.filter(
    j => !orderedJobs.some(x => x.workInstructionId === j.workInstructionId),
  )
  const orderedOtherJobs = orderJobs(otherJobs)

  orderedJobs.push(...orderedOtherJobs)

  return orderedJobs
}

export const jobsWithDeadline = (jobs: JobDto[]) => {
  return _(jobs)
    .filter(job => !!job.deadline && dayjs(job.deadline).diff(new Date(), 'minutes') <= 60)
    .orderBy(['workInstruction.deadline', 'id'])
    .value()
}

export const orderJobs = (jobs: JobDto[]) => {
  const orderedList = []

  //First show all discharge ordered
  const inboundJobsInWorkQueue = jobs.filter(
    x => x.operationType === OperationType.Inbound && !!x.workQueueId,
  )

  if (inboundJobsInWorkQueue.length) {
    const workQueuesArray = _(inboundJobsInWorkQueue)
      .map(job => job.workQueue)
      .compact()
      .uniqBy(queue => queue.id)
      .orderBy(queue => queue.order)
      .value()

    workQueuesArray.forEach(queue => {
      const inboundJobsOrdered = _(inboundJobsInWorkQueue)
        .filter(job => job.workQueueId === queue.id)
        .orderBy(job => job.workQueueOrder)
        .value()

      orderedList.push(...inboundJobsOrdered)
    })
  }

  //Then all discharge not in queues
  orderedList.push(...jobs.filter(x => x.operationType === OperationType.Inbound && !x.workQueueId))

  //Then all load ordered
  const outboundJobsInWorkQueue = jobs.filter(
    x => x.operationType === OperationType.Outbound && !!x.workQueueId,
  )

  if (outboundJobsInWorkQueue.length) {
    const workQueuesArray = _(outboundJobsInWorkQueue)
      .map(job => job.workQueue)
      .compact()
      .uniqBy(queue => queue.id)
      .orderBy(queue => queue.order)
      .value()

    workQueuesArray.forEach(queue => {
      const outboundJobsOrdered = _(outboundJobsInWorkQueue)
        .filter(job => job.workQueueId === queue.id)
        .orderBy(job => job.workQueueOrder)
        .value()

      orderedList.push(...outboundJobsOrdered)
    })
  }

  //Then all load not in queues
  orderedList.push(
    ...jobs.filter(x => x.operationType === OperationType.Outbound && !x.workQueueId),
  )

  //Then all restow
  orderedList.push(...jobs.filter(x => x.operationType === OperationType.Restow))

  //Then all service
  orderedList.push(...jobs.filter(x => x.operationType === OperationType.Service))

  //Then all internal
  orderedList.push(...jobs.filter(x => x.operationType === OperationType.Internal))

  return orderedList
}
